import { type FunctionComponent, type ReactElement, useEffect } from 'react';
import { type Maybe, type LocationFieldsFragment } from '../../../__generated__/graphql-client-types';
import { useUniversalCustomerLocation } from '../../../hooks/universal-location/universal-location.hooks';
import { StyledButton, TextButton, type TextButtonProps } from '../../buttons';
import { TextInput } from '../../inputs/input/text-input/text-input.component';
import { InputGroup } from '../../inputs/input-group/input-group.component';

const ZIP_CODE_CHANGER_STYLE_MAP = {
	DEFAULT: '',
	BOLD: 'font-medium',
	INLINE_BLOCK: 'dib pt1 pa0-ns'
};

export type ZipCodeChangerProps = {
	zipCodeChangerStyle?: keyof typeof ZIP_CODE_CHANGER_STYLE_MAP;
	textColor?: TextButtonProps['color'];
	formClass?: string;
	zipCodeLabelTransform?: (location?: LocationFieldsFragment) => Maybe<ReactElement>;
	submitButtonAutomation?: string;
	triggerAutomation?: string;
	inputAutomation?: string;
	underline?: boolean;
	changeButton?: boolean;
	onDisplayChange?: (isDisplayed: boolean) => void;
};

/**
 * Renders a link that shows the current zip code, and allows the user to change it
 */
export const ZipCodeChanger: FunctionComponent<ZipCodeChangerProps> = ({
	zipCodeChangerStyle = 'DEFAULT',
	textColor = 'grey',
	formClass = '',
	zipCodeLabelTransform,
	triggerAutomation,
	submitButtonAutomation,
	inputAutomation,
	underline = true,
	changeButton = false,
	onDisplayChange
}) => {
	const { setIsOpen, isOpen, location, hasError, canSubmit, errorMessage, onSubmit, register, isSubmitted } =
		useUniversalCustomerLocation('CUSTOMER_PROVIDED');

	const toggleModifyZipCode = () => {
		setIsOpen(!isOpen);
	};

	const customZipCodeButtonText = zipCodeLabelTransform ? zipCodeLabelTransform(location) : null;
	const zipCodeButtonText = location?.zipCode ? `Shipping to ${location.zipCode}` : 'Enter your shipping zip code';

	useEffect(() => {
		if (onDisplayChange) {
			onDisplayChange(isOpen);
		}
	}, [isOpen, onDisplayChange]);

	return (
		<>
			<span
				className={`${ZIP_CODE_CHANGER_STYLE_MAP[zipCodeChangerStyle]} ${changeButton && 'flex justify-between items-center'}`}
				data-testid={'zip-container'}>
				<TextButton
					onClick={toggleModifyZipCode}
					color={textColor}
					underline={underline}
					automationHook={triggerAutomation}
					textAlign={changeButton ? 'tl' : 'tc'}>
					{customZipCodeButtonText ? customZipCodeButtonText : zipCodeButtonText}
				</TextButton>
				{changeButton && (
					<TextButton onClick={toggleModifyZipCode} textAlign={'tr'} automationHook={'zip-change'} underlineHover={true}>
						{isOpen ? 'Cancel' : 'Change'}
					</TextButton>
				)}
			</span>

			{isOpen && (
				<form onSubmit={onSubmit} aria-label="change zip code form" className={formClass ?? ''}>
					<InputGroup className={`mt2 ${!changeButton && 'w-50-ns'}`}>
						<TextInput
							placeholder="Zip Code"
							ariaLabel="Zip Code"
							data-testid="zipCodeInput"
							automationHook={inputAutomation}
							{...register}
						/>
						<StyledButton
							buttonStyle="SECONDARY"
							disabled={Boolean(!canSubmit)}
							buttonType="submit"
							automationHook={submitButtonAutomation}>
							Apply
						</StyledButton>
					</InputGroup>
					{hasError && isSubmitted && (
						<div className="mt2" data-testid="zip-error">
							<span className="theme-error">* {errorMessage}</span>
						</div>
					)}
				</form>
			)}
		</>
	);
};
